import { FC, useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Popper,
  Paper,
} from '@mui/material';
import dayjs from 'dayjs';
import InputMask from 'react-input-mask';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClickAwayListener from '@mui/material/ClickAwayListener';

export interface EventsDatePickerProps {
  date: string; // ISO format: YYYY-MM-DD
  setDate: (newDate: string) => void;
}

const EventsDatePicker: FC<EventsDatePickerProps> = ({ date, setDate }) => {
  // Ensure we have a valid date, default to today if invalid
  const validDate = dayjs(date).isValid() ? dayjs(date) : dayjs();
  const [display, setDisplay] = useState(validDate.format('MM/DD/YYYY'));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dayjs(date).isValid()) {
      setDisplay(dayjs(date).format('MM/DD/YYYY'));
    } else {
      // If date is invalid, set to today
      const today = dayjs().format('YYYY-MM-DD');
      setDate(today);
      setDisplay(dayjs().format('MM/DD/YYYY'));
    }
  }, [date, setDate]);

  const handleCalendarChange = (val: Date | null) => {
    if (val) {
      const newISO = dayjs(val).format('YYYY-MM-DD');
      const newDisplay = dayjs(val).format('MM/DD/YYYY');
      setDate(newISO);
      setDisplay(newDisplay);
    }
    setCalendarOpen(false);
  };

  const handlePreviousDay = () => {
    const currentDate = dayjs(date).isValid() ? dayjs(date) : dayjs();
    const newDate = currentDate.subtract(1, 'day').format('YYYY-MM-DD');
    setDate(newDate);
  };

  const handleNextDay = () => {
    const currentDate = dayjs(date).isValid() ? dayjs(date) : dayjs();
    const newDate = currentDate.add(1, 'day').format('YYYY-MM-DD');
    setDate(newDate);
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {/* ← Button */}
      <IconButton
        onClick={handlePreviousDay}
        sx={{
          backgroundColor: 'rgb(68, 99, 136)',
          color: '#fff',
          borderRadius: '8px',
          width: '40px',
          height: '40px',
          boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgb(50, 75, 105)',
            transform: 'scale(1.05)',
          },
        }}
      >
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>

      {/* Masked Input with calendar icon */}
      <Box ref={anchorRef}>
        <InputMask
          mask="99/99/9999"
          maskPlaceholder="MM/DD/YYYY"
          alwaysShowMask
          value={display}
          onChange={(e) => {
            const val = e.target.value;
            setDisplay(val);
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
              const [mm, dd, yyyy] = val.split('/');
              const parsed = dayjs(`${yyyy}-${mm}-${dd}`);
              if (parsed.isValid()) {
                setDate(parsed.format('YYYY-MM-DD'));
              }
            }
          }}
        >
          {(inputProps: any) => (
            <TextField
              {...inputProps}
              variant="outlined"
              size="small"
              sx={{
                width: 170,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '0.95rem',
                  backgroundColor: '#fff',
                  color: '#3c5471',
                  fontFamily: 'Gotham',
                  '& fieldset': {
                    border: '1px solid rgba(0,0,0,0.23)',
                  },
                  '&:hover fieldset': {
                    border: '1px solid rgba(0,0,0,0.23)',
                  },
                  '&.Mui-focused fieldset': {
                    border: '1px solid rgba(0,0,0,0.23)',
                  },
                },
                '& input': {
                  textAlign: 'center',
                  fontFamily: 'Gotham',
                  letterSpacing: '1px',
                  fontWeight: 'bold',
                  color: '#3c5471',
                },
              }}
              placeholder="MM/DD/YYYY"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setCalendarOpen(true)} size="small">
                      <CalendarTodayIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </InputMask>
      </Box>

      {/* Calendar Popper */}
      <Popper
        open={calendarOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={() => setCalendarOpen(false)}>
          <Paper elevation={3} sx={{ p: 1, mt: 1 }}>
            <ReactDatePicker
              inline
              selected={dayjs(date).isValid() ? dayjs(date).toDate() : null}
              onChange={handleCalendarChange}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              // No maxDate restriction - allows future dates
            />
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* → Button */}
      <IconButton
        onClick={handleNextDay}
        sx={{
          backgroundColor: 'rgb(68, 99, 136)',
          color: '#fff',
          borderRadius: '8px',
          width: '40px',
          height: '40px',
          boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgb(50, 75, 105)',
            transform: 'scale(1.05)',
          },
        }}
      >
        <ArrowForwardIosIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default EventsDatePicker;

